import React from "react";
import { Modal, ModalBody } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faTimes, faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { useIntl } from "react-intl";
import { InstagramIcon } from "../Instagram/InstagramIcon";
import { FacebookIcon } from "../Facebook/FacebookIcon";
import InstagramLoginAuthButton from "./InstagramLoginAuthButton";
import { InstagramFacebookConnectButton } from "./InstagramFacebookConnectTrigger";
import "./InstagramConnectionMethodModal.scss";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onFacebookOAuthCompleted: (accessToken: string) => void;
}

export default function InstagramConnectionMethodModal({
  isOpen,
  onClose,
  onFacebookOAuthCompleted,
}: Props) {
  const intl = useIntl();

  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      centered
      size="lg"
      className="instagram-connection-method-modal"
    >
      <ModalBody className="p-4">
        <div className="text-center mb-4">
          <InstagramIcon size="4x" color="#E4405F" className="mb-3" />
          <h2 className="h4 mb-1">
            {intl.formatMessage({ id: "app.instagramLogin.modal.title" })}
          </h2>
          <p className="text-muted mb-0">
            {intl.formatMessage({ id: "app.instagramLogin.modal.subtitle" })}
          </p>
        </div>

        <div className="alert alert-info d-flex align-items-start mb-4">
          <FontAwesomeIcon icon={faInfoCircle} className="mr-2 mt-1" />
          <span>{intl.formatMessage({ id: "app.instagramLogin.modal.info" })}</span>
        </div>

        <div className="row">
          <div className="col-md-6 mb-3 mb-md-0">
            <div className="connection-card connection-card--limited h-100">
              <div className="connection-card__header">
                <InstagramIcon size="2x" color="#E4405F" />
                <h3 className="h6 mt-2">
                  {intl.formatMessage({ id: "app.instagramLogin.modal.instagramOnly.title" })}
                </h3>
                <span className="badge badge-secondary">
                  {intl.formatMessage({ id: "app.instagramLogin.modal.limitedAccess" })}
                </span>
              </div>
              <ul className="connection-card__features list-unstyled">
                <li>
                  <FontAwesomeIcon icon={faCheck} className="text-success mr-2" />
                  {intl.formatMessage({ id: "app.instagramLogin.modal.feature.basicMetrics" })}
                </li>
                <li>
                  <FontAwesomeIcon icon={faCheck} className="text-success mr-2" />
                  {intl.formatMessage({ id: "app.instagramLogin.modal.feature.schedule" })}
                </li>
                <li>
                  <FontAwesomeIcon icon={faCheck} className="text-success mr-2" />
                  {intl.formatMessage({ id: "app.instagramLogin.modal.feature.messages" })}
                </li>
                <li className="text-muted">
                  <FontAwesomeIcon icon={faTimes} className="mr-2" />
                  {intl.formatMessage({ id: "app.instagramLogin.modal.feature.fullMetrics" })}
                </li>
                <li className="text-muted">
                  <FontAwesomeIcon icon={faTimes} className="mr-2" />
                  {intl.formatMessage({ id: "app.instagramLogin.modal.feature.competitors" })}
                </li>
              </ul>
              <InstagramLoginAuthButton onSuccess={onClose} />
            </div>
          </div>

          <div className="col-md-6">
            <div className="connection-card connection-card--recommended h-100">
              <div className="connection-card__header">
                <div className="d-flex align-items-center justify-content-center">
                  <FacebookIcon size="2x" color="#1877F2" className="mr-2" />
                  <InstagramIcon size="2x" color="#E4405F" />
                </div>
                <h3 className="h6 mt-2">
                  {intl.formatMessage({ id: "app.instagramLogin.modal.facebook.title" })}
                </h3>
                <span className="badge badge-primary">
                  {intl.formatMessage({ id: "app.instagramLogin.modal.fullAccess" })}
                </span>
              </div>
              <p className="connection-card__description">
                {intl.formatMessage({ id: "app.instagramLogin.modal.facebook.description" })}
              </p>
              <p className="connection-card__requirement text-muted small">
                {intl.formatMessage({ id: "app.instagramLogin.modal.facebook.requirement" })}
              </p>
              <InstagramFacebookConnectButton
                className="btn btn-primary w-100"
                onCompleted={onFacebookOAuthCompleted}
              >
                {intl.formatMessage({ id: "app.instagramLogin.connect" })}
              </InstagramFacebookConnectButton>
            </div>
          </div>
        </div>
      </ModalBody>
    </Modal>
  );
}
